'use client';

/**
 * Fires select_item event for Google Analytics. *Note: Do not ever push PII into the data layer!!*
 *
 * @example
 * const { fireEvent } = useSelectItemEvent();
 * fireEvent(items);
 */

import { sendGTMEvent } from '@next/third-parties/google';
import {
  SearchItemSummaryData,
  SearchSelectItemData
} from 'app/[lang]/[esite]/(core)/(search-display)/_components/analytics-listener/types';
import {
  DEFAULT_BUSINESS_UNIT,
  EVENT_SELECT_ITEM
} from 'constants/analyticsConstants';
import { useCallback } from 'react';
import { OrderItemPart } from 'services/cart';
import { OrderItemPrice } from 'services/cart/types';
import { getAttributeValueForCart } from 'utils/gaEventAnalytics';

type AddToCartType = {
  item: OrderItemPart | SearchSelectItemData;
  priceData?: OrderItemPrice;
  partName: string;
  locationId: string;
  itemSummary: Product | SearchItemSummaryData | Product[];
  isSearchPageEvent?: boolean;
  itemListName?: string;
  itemListId?: string;
  index?: number;
  serialNumber?: string;
  fitment?: string;
  relatedModel?: string;
};

type BuildItemDataContext = {
  item: OrderItemPart | SearchSelectItemData;
  element;
  priceData?: OrderItemPrice;
  partName: string;
  locationId: string;
  isSearchPageEvent: boolean;
  index: number;
};

type BuildItemDataMeta = {
  availabilityMessage: string;
  productPrice: number;
  weight?: number | string;
};

type BuildItemDataOptions = {
  serialNumber?: string;
  fitment?: string;
  relatedModel?: string;
};

const getCategories = (element) => {
  if (element?.categoryPath) {
    return element.categoryPath.split('/');
  }

  if (element?.categoryPathList) {
    return element.categoryPathList[0]?.split(':');
  }

  return [];
};

const getFitment = (serialNumber, fitment, element = null) => {
  let exhaustiveFitment = serialNumber ? fitment : undefined;
  if (!exhaustiveFitment) {
    exhaustiveFitment = fitment;
  }

  if (!exhaustiveFitment) {
    exhaustiveFitment = element?.fitmentCheck;
  }

  return exhaustiveFitment;
};

const getDiscount = (
  isSearchPageEvent,
  priceData,
  item,
  actualProduct
): string => {
  let discount = !isSearchPageEvent
    ? priceData?.priceWithGST?.unformatted?.discountAmount
    : (item as SearchSelectItemData).discountAmount;

  if (!discount) {
    discount = actualProduct?.productPriceData?.price?.discountAmount;
  }

  return discount;
};

const buildItemData = (
  context: BuildItemDataContext,
  meta: BuildItemDataMeta,
  options: BuildItemDataOptions
) => {
  const {
    item,
    element,
    priceData,
    partName,
    locationId,
    isSearchPageEvent,
    index
  } = context;

  const { availabilityMessage, productPrice, weight } = meta;
  const { serialNumber, fitment, relatedModel } = options;
  const quantity = element?.quantity ?? item?.quantity;

  const [
    item_category,
    item_category2,
    item_category3,
    item_category4,
    item_category5
  ] = getCategories(element);

  return {
    affiliation: DEFAULT_BUSINESS_UNIT,
    item_id: item?.partNumber || element?.partNumber,
    item_name:
      element?.partNumber && element?.name
        ? `${element.partNumber}: ${element.name}`
        : partName,
    discount: getDiscount(isSearchPageEvent, priceData, item, element),
    index: index,
    item_brand: element?.manufacturer,
    item_category,
    item_category2,
    item_category3,
    item_category4,
    item_category5,
    location_id: locationId,
    price: productPrice || element?.unformattedTotalPrice,
    quantity: quantity ?? 1,
    p_inventory_status: availabilityMessage,
    p_weight: weight || element?.weight,
    p_serial_number: serialNumber,
    ...(!!relatedModel && { p_related_model: relatedModel }),
    p_check_fitment: getFitment(serialNumber, fitment, element)
  };
};

export const useSelectItemEvent = () => {
  const buildEvent = useCallback((params: AddToCartType) => {
    const {
      item,
      priceData,
      partName,
      locationId,
      itemSummary,
      isSearchPageEvent = false,
      itemListId,
      itemListName,
      index = 1,
      serialNumber,
      fitment,
      relatedModel
    } = params;

    const cartDetails = {
      cartId: (item as OrderItemPart).orderItemId
    };

    const { availabilityMessage } = !isSearchPageEvent
      ? getAttributeValueForCart(
          [priceData],
          (item as OrderItemPart).orderItemId
        )
      : (item as SearchSelectItemData);

    const { totalPrice = 0, weight } = item as SearchSelectItemData;
    const productPrice = !isSearchPageEvent
      ? priceData?.priceWithGST.unformatted.totalPrice
      : totalPrice;

    const itemData = Array.isArray(itemSummary) ? itemSummary : [itemSummary];
    const addItems = itemData.map((element) =>
      buildItemData(
        {
          item,
          element,
          priceData,
          partName: element?.name || partName,
          locationId,
          isSearchPageEvent,
          index: element?.index + 1 || index
        },
        {
          availabilityMessage,
          productPrice,
          weight
        },
        {
          serialNumber,
          fitment,
          relatedModel
        }
      )
    );

    return {
      event: EVENT_SELECT_ITEM,
      cartDetails,
      ecommerce: {
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: addItems
      }
    };
  }, []);

  const fireEvent = useCallback(
    (data: AddToCartType) => {
      sendGTMEvent({ ecommerce: null });
      sendGTMEvent(buildEvent(data));
    },
    [buildEvent]
  );

  return { fireEvent };
};
